import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import useIsMobile from 'lib/hooks/useIsMobile';
import { useI18n } from 'lib/i18n/context';
import useWeb3Wallet from 'lib/web3/useWallet';
import Skeleton from 'ui/shared/chakra/Skeleton';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import IconSvg from 'ui/shared/IconSvg';

import { ADDRESS_CONTRACT_KEYS } from '../../i18n';
import { useContractMethodFieldStyles } from './form/contractMethodFieldStyles';

interface Props {
  isLoading?: boolean;
}

const ContractConnectWallet = ({ isLoading }: Props) => {
  const { t } = useI18n();
  const web3Wallet = useWeb3Wallet({ source: 'Smart contracts' });
  const isMobile = useIsMobile();
  const styles = useContractMethodFieldStyles();

  const handleConnect = React.useCallback(() => {
    web3Wallet.connect();
  }, [ web3Wallet ]);

  const handleDisconnect = React.useCallback(() => {
    web3Wallet.disconnect();
  }, [ web3Wallet ]);

  const content = (() => {
    if (!web3Wallet.isConnected) {
      return (
        <>
          <Flex alignItems="center" gap={ 1.5 } flexShrink={ 0 }>
            <Box
              w="6px"
              h="6px"
              borderRadius="full"
              bg={ styles.inputPlaceholder }
              flexShrink={ 0 }
            />
            <Text fontSize="14px" fontWeight={ 500 } lineHeight="normal" color={ styles.inputText } whiteSpace="nowrap">
              { t(ADDRESS_CONTRACT_KEYS.labels.disconnected) }
            </Text>
          </Flex>
          <Flex
            as="button"
            type="button"
            alignItems="center"
            gap={ 1 }
            bg="transparent"
            border="none"
            p={ 0 }
            cursor={ web3Wallet.isOpen ? 'wait' : 'pointer' }
            onClick={ handleConnect }
            disabled={ web3Wallet.isOpen }
            _hover={{ opacity: web3Wallet.isOpen ? 1 : 0.85 }}
            _focus={{ outline: 'none' }}
          >
            { web3Wallet.isOpen ? (
              <Spinner size="sm" color={ styles.brandColor }/>
            ) : (
              <>
                <Text fontSize="14px" fontWeight={ 500 } lineHeight="normal" color={ styles.brandColor } whiteSpace="nowrap">
                  { t(ADDRESS_CONTRACT_KEYS.actions.connectWallet) }
                </Text>
                <IconSvg name="arrows/arrow-right-green" boxSize={ 4 } flexShrink={ 0 }/>
              </>
            ) }
          </Flex>
        </>
      );
    }

    return (
      <>
        <Flex
          alignItems="center"
          gap={ 1.5 }
          flex={ 1 }
          minW={ 0 }
          flexWrap="wrap"
        >
          <Box
            w="6px"
            h="6px"
            borderRadius="full"
            bg={ styles.brandColor }
            flexShrink={ 0 }
          />
          <Flex alignItems="center" gap={ 2 } minW={ 0 } flexWrap="wrap">
            <Text fontSize="14px" fontWeight={ 500 } lineHeight="normal" color={ styles.inputText } whiteSpace="nowrap">
              { t(ADDRESS_CONTRACT_KEYS.labels.connectedTo) }
            </Text>
            <AddressEntity
              address={{ hash: web3Wallet.address || '' }}
              truncation={ isMobile ? 'constant' : 'dynamic' }
              fontWeight={ 500 }
              fontSize="14px"
              noAltHash
            />
          </Flex>
        </Flex>
        <Flex
          as="button"
          type="button"
          alignItems="center"
          bg="transparent"
          border="none"
          p={ 0 }
          cursor="pointer"
          onClick={ handleDisconnect }
          _hover={{ opacity: 0.85 }}
          _focus={{ outline: 'none' }}
          flexShrink={ 0 }
        >
          <Text fontSize="14px" fontWeight={ 500 } lineHeight="normal" color={ styles.brandColor } whiteSpace="nowrap">
            { t(ADDRESS_CONTRACT_KEYS.actions.disconnect) }
          </Text>
        </Flex>
      </>
    );
  })();

  return (
    <Skeleton isLoaded={ !isLoading }>
      <Flex
        bg={ styles.inputBg }
        border="1px solid"
        borderColor={ styles.inputBorder }
        borderRadius="8px"
        p={ 4 }
        alignItems="center"
        gap={ 3 }
        w="100%"
        flexWrap="wrap"
      >
        { content }
      </Flex>
    </Skeleton>
  );
};

const Fallback = () => null;

export default config.features.blockchainInteraction.isEnabled ? ContractConnectWallet : Fallback;
